import { useState } from 'react';
import Button from '../../../components/base/Button';

interface FormErrors {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
}

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const validateField = (name: string, value: string): string | undefined => {
    switch (name) {
      case 'name':
        if (!value.trim()) return 'Name is required';
        if (value.trim().length < 2) return 'Name must be at least 2 characters';
        return undefined;
      case 'email':
        if (!value.trim()) return 'Email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email address';
        return undefined;
      case 'subject':
        if (!value) return 'Please select a subject';
        return undefined;
      case 'message':
        if (!value.trim()) return 'Message is required';
        if (value.trim().length < 10) return 'Message must be at least 10 characters';
        return undefined;
      default:
        return undefined;
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setTouched({ ...touched, [name]: true });
    setErrors({ ...errors, [name]: validateField(name, value) });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear error when user starts typing
    if (touched[name]) {
      setErrors({ ...errors, [name]: validateField(name, value) });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate all fields
    const newErrors: FormErrors = {};
    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key as keyof typeof formData]);
      if (error) newErrors[key as keyof FormErrors] = error;
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setTouched({
        name: true,
        email: true,
        subject: true,
        message: true,
      });
      return;
    }

    setIsSubmitting(true);

    // Simulate API call
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      console.log('Form submitted:', formData);
      setSubmitStatus('success');

      // Reset form after success
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          company: '',
          subject: '',
          message: '',
        });
        setTouched({});
        setSubmitStatus('idle');
      }, 3000);
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-200 p-8 lg:p-10">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-[#22577A] mb-3">Send us a message</h2>
        <p className="text-gray-600">
          Fill out the form below and we'll get back to you within 24 hours
        </p>
      </div>

      {/* Success Message */}
      {submitStatus === 'success' && (
        <div className="mb-6 p-4 bg-green-50 border-2 border-green-200 rounded-xl flex items-center gap-3 animate-fade-in">
          <i className="ri-checkbox-circle-fill text-green-600 text-2xl"></i>
          <div>
            <p className="font-semibold text-green-800">Message sent successfully!</p>
            <p className="text-sm text-green-700">We'll get back to you within 24 hours.</p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {submitStatus === 'error' && (
        <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl flex items-center gap-3 animate-fade-in">
          <i className="ri-error-warning-fill text-red-600 text-2xl"></i>
          <div>
            <p className="font-semibold text-red-800">Oops! Something went wrong.</p>
            <p className="text-sm text-red-700">Please try again later.</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
              Full Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-colors text-base ${
                touched.name && errors.name
                  ? 'border-red-300 focus:border-red-500'
                  : 'border-gray-200 focus:border-[#66b3ff]'
              }`}
              placeholder="John Doe"
              disabled={isSubmitting}
            />
            {touched.name && errors.name && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                <i className="ri-error-warning-line"></i>
                {errors.name}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-colors text-base ${
                touched.email && errors.email
                  ? 'border-red-300 focus:border-red-500'
                  : 'border-gray-200 focus:border-[#66b3ff]'
              }`}
              placeholder="john@example.com"
              disabled={isSubmitting}
            />
            {touched.email && errors.email && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                <i className="ri-error-warning-line"></i>
                {errors.email}
              </p>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
              Company
            </label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#66b3ff] focus:outline-none transition-colors text-base"
              placeholder="Your Company"
            />
          </div>

          <div>
            <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">
              Subject *
            </label>
            <select
              id="subject"
              name="subject"
              required
              value={formData.subject}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-colors text-base ${
                touched.subject && errors.subject
                  ? 'border-red-300 focus:border-red-500'
                  : 'border-gray-200 focus:border-[#66b3ff]'
              }`}
              disabled={isSubmitting}
            >
              <option value="">Select a subject</option>
              <option value="sales">Sales Inquiry</option>
              <option value="support">Technical Support</option>
              <option value="partnership">Partnership Opportunity</option>
              <option value="feedback">Feedback</option>
              <option value="other">Other</option>
            </select>
            {touched.subject && errors.subject && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                <i className="ri-error-warning-line"></i>
                {errors.subject}
              </p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
            Message *
          </label>
          <textarea
            id="message"
            name="message"
            required
            value={formData.message}
            onChange={handleChange}
            onBlur={handleBlur}
            rows={6}
            className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-colors text-base resize-none ${
              touched.message && errors.message
                ? 'border-red-300 focus:border-red-500'
                : 'border-gray-200 focus:border-[#66b3ff]'
            }`}
            placeholder="Tell us how we can help you..."
            disabled={isSubmitting}
          />
          {touched.message && errors.message && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
              <i className="ri-error-warning-line"></i>
              {errors.message}
            </p>
          )}
          <p className="mt-1 text-sm text-gray-500">
            {formData.message.length}/500 characters
          </p>
        </div>

        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            id="privacy"
            required
            className="mt-1 w-5 h-5 rounded border-2 border-gray-300 text-[#22577A] focus:ring-[#66b3ff]"
          />
          <label htmlFor="privacy" className="text-sm text-gray-600">
            I agree to the{' '}
            <a href="#" className="text-[#22577A] hover:text-[#38a3a5] font-semibold">
              Privacy Policy
            </a>{' '}
            and{' '}
            <a href="#" className="text-[#22577A] hover:text-[#38a3a5] font-semibold">
              Terms of Service
            </a>
          </label>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className={`w-full px-8 py-4 rounded-xl font-semibold text-lg transition-all flex items-center justify-center gap-2 ${
            isSubmitting
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-gradient-to-r from-[#22577A] to-[#38a3a5] text-white hover:shadow-xl hover:scale-[1.02]'
          }`}
        >
          {isSubmitting ? (
            <>
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Sending...
            </>
          ) : (
            <>
              <i className="ri-send-plane-fill"></i>
              Send Message
            </>
          )}
        </button>
      </form>
    </div>
  );
}
